import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";
import { ApiError } from "@/client/lib/api";
import { pageTitle } from "@/client/lib/page-title";
import { PENDING_MS } from "@/client/lib/pending";
import {
  InvoiceClientPage,
  InvoiceClientPending,
  InvoiceLinkGone,
} from "@/features/invoices/components/invoice-client-page";
import { invoiceLinkQuery } from "@/features/invoices/invoice-query";

/** What the server says about a withdrawn link: whom the client should ask for a new one. */
const withdrawn = z.object({
  company: z.object({ name: z.string(), billingEmail: z.string().nullable() }),
});

/** Outside the signed-in layout: the client has no account, and the link is the credential. */
export const Route = createFileRoute("/i/$token")({
  loader: ({ context, params }) =>
    context.queryClient.ensureQueryData(invoiceLinkQuery(params.token)),
  head: async ({ loaderData }) => {
    const { invoicesCopy } = await import("@/features/invoices/copy");
    return {
      meta: [
        {
          title: pageTitle(
            loaderData
              ? invoicesCopy.clientPage.invoice(loaderData.document.number)
              : invoicesCopy.link.gone,
          ),
        },
      ],
    };
  },
  pendingMs: PENDING_MS,
  pendingComponent: () => (
    <InvoiceClientPending token={Route.useParams().token} />
  ),
  component: Linked,
  errorComponent: ({ error }) => {
    if (!(error instanceof ApiError) || error.code !== "NOT_FOUND") throw error;
    const known = withdrawn.safeParse(error.details);
    return (
      <InvoiceLinkGone company={known.success ? known.data.company : null} />
    );
  },
});

function Linked() {
  const { token } = Route.useParams();
  const { data } = useSuspenseQuery(invoiceLinkQuery(token));
  return <InvoiceClientPage token={token} linked={data} />;
}
